// public
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { getSizeData, SIZE_S, SIZE_M, SIZE_L } from './sizes';
import {
  getRankData,
  RANK_PRIMARY,
  RANK_SECONDARY,
  RANK_TERTIARY,
  RANK_TEXT,
  RANK_TEXT_REGULAR,
} from './ranks';
import testable from '@xingternal/malt-util/testable';
import { QA_MALT } from '@xingternal/malt-constants';
import { base as styles, sizes as styleSize } from '../style';

import { withFlavour } from '@xingternal/flavour';
import ButtonXdl from '@xingternal/button';

const LinkElement = testable(QA_MALT, 'a');
const RouterLinkElement = testable(QA_MALT, Link);
const ButtonElement = testable(QA_MALT, 'button');

const ButtonOdl = ({
  icon: IconComp,
  buttonRef,
  children,
  loading,
  disabled = false,
  href,
  target,
  to,
  onClick = () => {},
  type = 'button',
  handlers = {},
  size,
  sizeConfined,
  sizeWide,
  rank,
  className,
  noPadding = false,
  ...other
}) => {
  let Element = ButtonElement;
  if (to) {
    Element = RouterLinkElement;
  } else if (href) {
    Element = LinkElement;
  }
  const hasCopy = Boolean(children);
  const { iconSize, buttonSizeClassName } = getSizeData(size, !hasCopy);
  const rankClassName = getRankData(rank);

  const buttonClassName = classnames(
    rankClassName,
    styles.button,
    buttonSizeClassName,
    className,
    {
      [styles.noPadding]: noPadding,
      [styleSize[`confined-${sizeConfined}`]]: sizeConfined,
      [styleSize[`wide-${sizeWide}`]]: sizeWide,
    }
  );
  const textClassName = classnames(styles.text, loading && styles.textLoading);
  const iconClassName = classnames(
    styleSize.icon,
    styles.text,
    loading && styles.textLoading
  );
  const onClickHandler =
    disabled || loading ? e => e.preventDefault() : onClick;

  return (
    <Element
      innerRef={buttonRef}
      href={href}
      to={to}
      target={href && target ? target : null}
      className={buttonClassName}
      disabled={disabled || loading}
      onClick={onClickHandler}
      type={type}
      {...handlers}
      {...other}
    >
      <div className={classnames(styles.inner, styleSize.inner)}>
        {IconComp && <IconComp className={iconClassName} size={iconSize} />}
        {loading && (
          <span className={styles.loading}>
            <span
              className={classnames(
                styles.loadingDot,
                styleSize.loadingDotIconButton
              )}
            />
            <span
              className={classnames(
                styles.loadingDot,
                styleSize.loadingDotIconButton
              )}
            />
            <span
              className={classnames(
                styles.loadingDot,
                styleSize.loadingDotIconButton
              )}
            />
          </span>
        )}
        <span className={textClassName}>{children}</span>
      </div>
    </Element>
  );
};

const Button = ({
  flavour,
  rank,
  size,
  sizeConfined,
  sizeWide,
  className,
  ...props
}) => {
  if (flavour === 'xdl') {
    const sizes = { [SIZE_S]: 'small', [SIZE_M]: 'medium', [SIZE_L]: 'large' };

    const sizesOnConfined = {
      s: 'smallOnConfined',
      m: 'mediumOnConfined',
      l: 'largeOnConfined',
    };
    const sizesOnWide = {
      s: 'smallOnWide',
      m: 'mediumOnWide',
      l: 'largeOnWide',
    };
    const ranks = {
      [RANK_PRIMARY]: 'primary',
      [RANK_SECONDARY]: 'secondary',
      [RANK_TERTIARY]: 'tertiary',
      [RANK_TEXT]: 'text',
      [RANK_TEXT_REGULAR]: 'textRegular',
    };

    const xdlProps = {
      ...props,
      [ranks[rank]]: true,
      [sizes[size]]: true,
      [sizesOnConfined[sizeConfined]]: true,
      [sizesOnWide[sizeWide]]: true,
      skin: { button: className },
    };
    return <ButtonXdl {...xdlProps} />;
  } else {
    return (
      <ButtonOdl
        rank={rank}
        size={size}
        sizeConfined={sizeConfined}
        sizeWide={sizeWide}
        className={className}
        {...props}
      />
    );
  }
};

Button.propTypes = ButtonOdl.propTypes = {
  /** @ignore */
  size: PropTypes.symbol.isRequired,
  /** @ignore */
  rank: PropTypes.symbol.isRequired,
  /** inserts the button text */
  children: PropTypes.node,
  /** add a custom className */
  className: PropTypes.string,
  /** set the tablet and up size for input (s, m, l) */
  sizeConfined: PropTypes.oneOf(['s', 'm', 'l']),
  /** set the desktop and up size for input (s, m, l) */
  sizeWide: PropTypes.oneOf(['s', 'm', 'l']),
  /** remove the padding of the button */
  noPadding: PropTypes.bool,
  /** define an Icon similar to how they are defined in @xingternal/malt-icon  */
  icon: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
  /** set disabled state */
  disabled: PropTypes.bool,
  /** set loading state. Shows a loading spinner and disables the button */
  loading: PropTypes.bool,
  /** If you want to link instead of invoking an onClick, set the href attribute. Element will be an `a` tag then.  */
  href: PropTypes.string,
  /** Target for open link */
  target: PropTypes.string,
  /** Define a location descriptor here if the card should be linked to a pre-defined route. You can use a `string` or an `object`. Read more about the [`Link` component](https://github.com/ReactTraining/react-router/blob/master/packages/react-router-dom/docs/api/Link.md). */
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** set the onClick function here */
  onClick: PropTypes.func,
  /** set the element type */
  type: PropTypes.string,
  /** pass any other handlers you need */
  handlers: PropTypes.object,
  /** define a testId for the button */
  testId: PropTypes.string,
  /** define a tabIndex for the button */
  tabIndex: PropTypes.string,
  /** When `true`, clicking the link will replace the current entry in the history stack instead of adding a new one. Only relevant when `to` is set as well. */
  replace: PropTypes.bool,
  /** callback to store a reference to the DOM element */
  buttonRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

export default withFlavour(Button);
